import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setSelectedTemplate } from '../../features/templates/templatesSlice';
import { useGetSavedTemplateByIdQuery, useGetSavedTemplateByCmsIdQuery, useGetTemplateByIdQuery } from '../../features/templates/templatesApi';
import TemplateEditorPage from './TemplateEditorPage';

export default function TemplateEditorPageWrapper() {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('id');
  const isCmsId = searchParams.get('isCmsId') === 'true';
  const dispatch = useAppDispatch();
  
  const reduxSelectedTemplate = useAppSelector((state) => state.templates?.selectedTemplate);

  // Trigger queries if templateId is present in URL query params
  const { data: savedTemplateById, error: savedByIdError } = useGetSavedTemplateByIdQuery(templateId || '', { skip: !templateId || isCmsId });
  const { data: savedTemplateByCmsId, error: savedByCmsIdError } = useGetSavedTemplateByCmsIdQuery(templateId || '', { skip: !templateId || !isCmsId });
  const { data: libraryTemplate, error: libraryError } = useGetTemplateByIdQuery(templateId || '', { skip: !templateId || isCmsId });

  const savedTemplate = isCmsId ? savedTemplateByCmsId : savedTemplateById;
  const savedError = isCmsId ? savedByCmsIdError : savedByIdError;

  // 1. Sync saved template to Redux when loaded
  useEffect(() => {
    if (savedTemplate && templateId) {
      const { id: dataId, ...otherData } = savedTemplate.data || {};
      dispatch(setSelectedTemplate({
        id: savedTemplate._id,
        cmsContentId: savedTemplate.cmsContentId,
        originTemplateId: dataId,
        name: savedTemplate.name,
        previewUrl: savedTemplate.thumbnail,
        isCustom: true,
        category: savedTemplate.category || (savedTemplate.data && savedTemplate.data.category),
        ...otherData
      }));
    }
  }, [savedTemplate, templateId, dispatch]);

  // 2. Sync library template to Redux when loaded
  useEffect(() => {
    if (libraryTemplate && templateId) {
      dispatch(setSelectedTemplate({
        id: libraryTemplate._id,
        name: libraryTemplate.name,
        previewUrl: libraryTemplate.thumbnail,
        category: libraryTemplate.category,
        width: libraryTemplate.data.width,
        height: libraryTemplate.data.height,
        layers: libraryTemplate.data.layers,
        elements: libraryTemplate.data.elements,
        layerPositions: libraryTemplate.data.layerPositions,
        textContents: libraryTemplate.data.textContents,
        textStyles: libraryTemplate.data.textStyles,
        imageSettings: libraryTemplate.data.imageSettings,
        shapeSettings: libraryTemplate.data.shapeSettings,
        backgroundSettings: libraryTemplate.data.backgroundSettings,
        scrollerSettings: libraryTemplate.data.scrollerSettings
      }));
    }
  }, [libraryTemplate, templateId, dispatch]);

  // If a template ID is specified in URL but Redux doesn't have it yet (or is still loading it), show a loading spinner
  const hasIdInUrl = !!templateId;
  const isReduxSynced = reduxSelectedTemplate && (
    reduxSelectedTemplate.id === templateId ||
    (reduxSelectedTemplate.cmsContentId && reduxSelectedTemplate.cmsContentId === templateId)
  );
  const isLoading = hasIdInUrl && !isReduxSynced;
  const hasError = hasIdInUrl && (isCmsId ? !!savedError : (!!savedError && !!libraryError));

  if (hasError) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-[9999] dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4 text-center p-6 max-w-md">
          <div className="bg-red-50 dark:bg-red-900/30 p-3 rounded-full text-red-600 dark:text-red-400">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Failed to Load Design</h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            We couldn't retrieve the template you specified. It might have been deleted, or you might not have permission to edit it.
          </p>
          <button
            onClick={() => window.location.href = '/home'}
            className="px-4 py-2 bg-[#30A84B] text-white rounded-[6px] hover:bg-[#288e3e] transition-colors font-semibold text-sm"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-[9999] dark:bg-gray-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-[#30A84B] border-t-transparent rounded-full animate-spin" />
          <p className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest animate-pulse">
            Syncing Design...
          </p>
        </div>
      </div>
    );
  }

  return <TemplateEditorPage />;
}
